import { DatePipe, SlicePipe } from '@angular/common';
import { Component, computed, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  FwButtonComponent,
  FwCell,
  FwCellDef,
  FwColumnDef,
  FwDialogConfirmComponent,
  FwDialogService,
  FwHeaderCell,
  FwHeaderCellDef,
  FwHeaderRow,
  FwHeaderRowDef,
  FwIconComponent,
  FwRow,
  FwRowDef,
  FwTableComponent,
  FwTextInputComponent,
} from '@flywheel-io/vision';
import countBy from 'lodash/countBy';
import get from 'lodash/get';
import orderBy from 'lodash/orderBy';
import { debounceTime, filter, first, map, startWith, switchMap } from 'rxjs/operators';

import { ApiParams, CoreApiService, queryAll } from '../../core-api.service';
import { FileModule } from '../../files/file.module';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { ProfileService } from '../../profile/profile.service';
import { ProjectsService } from '../../projects/projects.service';
import { FwEllipsisComponent } from '../../shared/components/fw-ellipsis/fw-ellipsis.component';
import { getContainerProjectId, getContainerType, getRoleMatrix } from '../../shared/container.utils';
import { ContainerAccess } from '../../shared/enums/container-access.enum';
import { JobPriority } from '../../shared/enums/job-priority.enum';
import { JobState } from '../../shared/enums/job-state.enum';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { jobStatusMap } from '../../shared/job.utils';
import { Container } from '../../shared/models/container.model';
import { File } from '../../shared/models/file.model';
import { Job, JobInput } from '../../shared/models/job.model';
import { JobLogDialog } from '../job-log/job-log-dialog.component';

interface JobInputExt extends JobInput {
  tooltip: string;
}

interface FileExt extends File {
  tooltip: string;
}

interface JobExt extends Job {
  name: string;
  inputLimit: number;
  inputList: JobInputExt[];
  outputLimit: number;
  outputList: FileExt[];
}

@Component({
  selector: 'job-provenance',
  templateUrl: './job-provenance.component.html',
  styleUrls: ['./job-provenance.component.scss'],
  imports: [
    DatePipe,
    FileModule,
    FwButtonComponent,
    FwCell,
    FwCellDef,
    FwColumnDef,
    FwEllipsisComponent,
    FwHeaderCell,
    FwHeaderCellDef,
    FwHeaderRow,
    FwHeaderRowDef,
    FwIconComponent,
    FwRow,
    FwRowDef,
    FwTableComponent,
    FwTextInputComponent,
    ReactiveFormsModule,
    SlicePipe,
  ],
})
export class JobProvenanceComponent {
  private readonly dialog = inject(FwDialogService);
  private readonly logger = inject(LoggingService);
  private readonly notifications = inject(NotificationService);
  private readonly coreApi = inject(CoreApiService);
  private readonly profileService = inject(ProfileService);
  private readonly projectsService = inject(ProjectsService);
  private readonly destroyRef = inject(DestroyRef);

  container = input.required<Container>();
  pageSize = input(50);

  readonly displayedColumns = ['gear', 'priority', 'input', 'output', 'actions'];
  readonly jobStatusMap = jobStatusMap;
  readonly jobPriorityMap: Record<JobPriority, string> = {
    [JobPriority.Critical]: 'Critical',
    [JobPriority.High]: 'High',
    [JobPriority.Medium]: 'Medium',
    [JobPriority.Low]: 'Low',
  };

  gearNameFilter = new FormControl<string>('');
  protected isAdmin = toSignal(
    toObservable(this.container).pipe(
      switchMap((container) => {
        const projectId = getContainerProjectId(container);
        if (projectId) {
          return this.projectsService
            .getProjectPermissions(projectId)
            .pipe(map((perms) => perms.includes(PermissionAction.JobsManage)));
        }
        return this.profileService.getProfile().pipe(
          first(),
          map((profile) => {
            const roleMatrix = getRoleMatrix(container, profile);
            return roleMatrix[ContainerAccess.Admin];
          }),
        );
      }),
    ),
    { initialValue: false },
  );

  private page = signal(1);
  private refreshTrigger = signal(0);

  private gearFilter = toSignal(this.gearNameFilter.valueChanges.pipe(startWith(''), debounceTime(200)), {
    initialValue: '',
  });

  private allJobs = toSignal(
    toObservable(
      computed(() => ({
        container: this.container(),
        gearFilter: this.gearFilter(),
        _refresh: this.refreshTrigger(),
      })),
    ).pipe(
      switchMap(({ container, gearFilter }) => {
        const containerType = getContainerType(container);
        const params: ApiParams = { sort: 'created:desc' };
        const filters: string[] = [];
        if (gearFilter) {
          filters.push(`gear_info.name=~${gearFilter}`);
        }
        filters.push(`parents.${containerType}=${container._id}`);
        if (filters.length > 0) {
          params.filter = filters.join(',');
        }
        return queryAll(() => this.coreApi.projects.jobs(container.parents.project, params)).pipe(
          map((jobs) => this.toJobExt(jobs)),
        );
      }),
    ),
  );

  protected totalJobs = computed(() => this.allJobs()?.length ?? 0);

  protected displayedJobs = computed(() => {
    const all = this.allJobs();
    if (all === undefined) {
      return null;
    }
    return orderBy(all.slice(0, this.pageSize() * this.page()), ['created'], ['desc']) as JobExt[];
  });

  protected summary = computed(() => {
    const all = this.allJobs();
    if (all === undefined) {
      return null;
    }
    const { failed, pending, running, complete } = countBy(all, 'state');
    return {
      complete: complete || 0,
      failed: failed || 0,
      pending: pending || 0,
      running: running || 0,
      total: all.length,
    };
  });

  constructor() {
    effect(() => {
      this.container();
      this.page.set(1);
    });
  }

  refreshJobs(): void {
    this.page.set(1);
    this.refreshTrigger.update((n) => n + 1);
  }

  nextPage(): void {
    this.page.update((n) => n + 1);
  }

  retryJob(job: Job): void {
    this.coreApi.jobs.retry(job._id).subscribe({
      next: () => {
        this.notifications.success('jobs.retry.success');
        this.refreshJobs();
      },
      error: (error) => {
        this.logger.error(error);
        this.notifications.error('jobs.retry.error');
      },
    });
  }

  cancelJob(job: Job): void {
    const confirmDialog = this.dialog.open(FwDialogConfirmComponent);
    if (!confirmDialog.componentRef) {
      return;
    }
    confirmDialog.componentRef.setInput('title', 'Cancel Job');
    confirmDialog.componentRef.setInput('contentTitle', 'Are you sure you want to cancel the job?');
    confirmDialog.componentRef.setInput('confirmColor', 'danger');
    confirmDialog.componentRef.setInput('confirmButtonText', 'Cancel Job');
    confirmDialog.componentRef.setInput('showConfirmButtonIcon', false);
    confirmDialog.closed
      .pipe(
        filter(Boolean),
        switchMap(() => this.coreApi.jobs.put(job._id, { state: JobState.Cancelled })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.notifications.success('jobs.cancel.success');
          this.refreshJobs();
        },
        error: (error) => {
          this.logger.error(error);
          this.notifications.error('jobs.cancel.error');
        },
      });
  }

  displayJobLog(job: JobExt): void {
    this.dialog.open(JobLogDialog, { data: { jobId: job._id, jobName: job.name } });
  }

  private toJobExt(jobs: Job[]): JobExt[] {
    return jobs.map((job) => {
      const inputList = (job.inputs ?? [])
        .filter((input) => input.name?.length > 0)
        .map((input) => {
          const inputConfig = get(job, ['config', 'inputs', input.input, 'object']) ?? {};
          const tooltip = [`Name: ${input.name}`];
          if (inputConfig.origin) {
            tooltip.push(`Origin Type: ${inputConfig.origin.type}`, `Origin: ${inputConfig.origin.id}`);
          }
          if (inputConfig.version) {
            tooltip.push(`Version: ${inputConfig.version}`);
          }
          return {
            ...input,
            tooltip: tooltip.join('\n'),
            ...(inputConfig.file_id ? { file: inputConfig } : {}),
          };
        });
      const outputList = (job.outputs ?? job.saved_files?.map((name) => ({ name }) as File) ?? []).map((output) => {
        const tooltip = [`Name: ${output.name}`];
        if (output.version) {
          tooltip.push(`Version: ${output.version}`);
        }
        return { ...output, tooltip: tooltip.join('\n') };
      });
      return {
        ...job,
        inputLimit: 1,
        inputList,
        name: `${job.gear_info.name} v${job.gear_info.version}`,
        outputLimit: 1,
        outputList,
      };
    });
  }
}
